import { toValue } from '@collector/shared-utils'
import { Incident } from '@collector/sportsapi-client'
import {
  FilterSubParticipantsOptions,
  isSubParticipantSetting,
  ShouldLoadOppositeSubParticipants,
  SubParticipantSettings,
  SubParticipantType,
} from '@desktop/views/Relation/Sports/configuration'
import { injectHistoryIncidentsConfig } from '@desktop/views/Relation/Sports/provide-inject'

type IncidentSettings =
  | ({
      type: SubParticipantType
    } & Required<SubParticipantSettings>)
  | undefined

export function useHistoryIncidentParticipantSettings(incident: Incident): {
  getSubParticipantSettings(
    subParticipantType: SubParticipantType,
  ): IncidentSettings
} {
  // inject
  const historyIncidentsConfig = injectHistoryIncidentsConfig() || {}

  // methods
  function getSetting(
    subParticipantType: SubParticipantType,
  ): string | SubParticipantSettings | undefined {
    return toValue(
      historyIncidentsConfig[incident.incident_id]?.[subParticipantType],
      incident,
    )
  }

  function getSettingRequired(subParticipantType: SubParticipantType): boolean {
    const setting = getSetting(subParticipantType)

    if (isSubParticipantSetting(setting)) {
      return !!setting.required
    }

    return false
  }

  function getSettingName(
    subParticipantType: SubParticipantType,
  ): string | undefined {
    const setting = getSetting(subParticipantType)

    if (isSubParticipantSetting(setting)) {
      return setting.name
    }

    return setting
  }

  function getSettingShouldLoadOppositeParticipant(
    subParticipantType: SubParticipantType,
  ): ShouldLoadOppositeSubParticipants {
    const setting = getSetting(subParticipantType)

    if (isSubParticipantSetting(setting)) {
      return setting.shouldLoadOppositeSubParticipants ?? false
    }

    return false
  }

  function getSettingFilterSubParticipantsOptionsHandler(
    subParticipantType: SubParticipantType,
  ): FilterSubParticipantsOptions {
    const setting = getSetting(subParticipantType)

    if (isSubParticipantSetting(setting)) {
      return setting.filterSubParticipantsOptions ?? ((options) => options)
    }

    return (options) => options
  }

  function getSettingKeepSelectedOption(
    subParticipantType: SubParticipantType,
  ): boolean {
    const setting = getSetting(subParticipantType)

    if (isSubParticipantSetting(setting)) {
      return setting.keepSelectedOption ?? false
    }

    return false
  }

  function getSettingIsAvailable(
    subParticipantType: SubParticipantType,
  ): SubParticipantSettings['isAvailable'] {
    const setting = getSetting(subParticipantType)

    if (isSubParticipantSetting(setting)) {
      return setting.isAvailable
    }

    return undefined
  }

  function getSubParticipantSettings(
    subParticipantType: SubParticipantType,
  ): IncidentSettings {
    const name = getSettingName(subParticipantType)
    if (!name) return undefined

    const required = getSettingRequired(subParticipantType)
    const shouldLoadOppositeSubParticipants =
      getSettingShouldLoadOppositeParticipant(subParticipantType)
    const filterSubParticipantsOptions =
      getSettingFilterSubParticipantsOptionsHandler(subParticipantType)
    const keepSelectedOption = getSettingKeepSelectedOption(subParticipantType)
    const isAvailable =
      getSettingIsAvailable(subParticipantType) ?? (() => true)

    return {
      name,
      required,
      type: subParticipantType,
      keepSelectedOption,
      shouldLoadOppositeSubParticipants,
      filterSubParticipantsOptions,
      isAvailable,
    }
  }

  return {
    getSubParticipantSettings,
  }
}
